// Card de configuração do cut-over CA → sistema.
// Após a data definida, lançamentos com data_competencia >= cut_over ficam editáveis
// no sistema (origem CA), e o sync ignora esses períodos.
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, Button, Tag, Typography, Flex, Statistic, DatePicker, App as AntApp,
} from 'antd';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import type { CutOverCfg } from './types';

const { Title, Text } = Typography;

export default function CutOverConfig() {
  const { message, modal } = AntApp.useApp();
  const [cfg, setCfg] = useState<CutOverCfg | null>(null);
  const [editando, setEditando] = useState(false);
  const [novaData, setNovaData] = useState<any>(null);
  const [salvando, setSalvando] = useState(false);
  const [loading, setLoading] = useState(true);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/conta-azul/config`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setCfg(d || null);
      setNovaData(d?.data_cut_over ? dayjs(d.data_cut_over) : null);
    } catch (e: any) {
      console.warn('Falha ao carregar config CA:', e.message);
    } finally { setLoading(false); }
  }
  useEffect(() => { carregar(); }, []);

  async function salvar() {
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/conta-azul/config`,
        { data_cut_over: novaData ? novaData.format('YYYY-MM-DD') : null },
        { headers: { Authorization: token } });
      message.success('Data de cut-over atualizada');
      setEditando(false);
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function togglePausa(pausado: boolean) {
    modal.confirm({
      title: pausado ? 'Pausar sync da Conta Azul?' : 'Retomar sync?',
      content: pausado
        ? 'Para o sync completamente até você reativar. Use em emergências (ex: bug no sync corrompendo dados).'
        : 'Retoma o sync normal respeitando a data de cut-over.',
      okText: pausado ? 'Pausar' : 'Retomar',
      okType: pausado ? 'danger' : 'primary',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/conta-azul/config`,
            { sync_pausado: pausado },
            { headers: { Authorization: token } });
          message.success(pausado ? 'Sync pausado' : 'Sync retomado');
          await carregar();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  // Parse seguro YYYY-MM-DD → DD/MM/YYYY (evita conversão timezone)
  const dataAtual = cfg?.data_cut_over
    ? (() => {
        const s = String(cfg.data_cut_over).slice(0, 10);
        const [y, m, d] = s.split('-');
        return (y && m && d) ? `${d}/${m}/${y}` : s;
      })()
    : '— não definida —';

  return (
    <Card size="small" style={{ marginBottom: 16, background: cfg?.sync_pausado ? '#FFF1F0' : '#F6FFED' }}>
      <Flex justify="space-between" align="flex-start" wrap gap={16}>
        <div style={{ flex: 1 }}>
          <Title level={5} style={{ margin: 0, color: '#020D7F' }}>
            🗓 Cut-over CA → sistema
          </Title>
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
            Lançamentos com <strong>data de vencimento</strong> a partir desta data ficam editáveis no sistema, e o sync CA <strong>não toca</strong> nesses períodos.
            Lançamentos com vencimento anterior continuam sendo sincronizados normalmente.
          </Text>

          <Flex gap={20} style={{ marginTop: 12 }} align="center" wrap>
            {editando ? (
              <>
                <DatePicker value={novaData} onChange={setNovaData}
                  format="DD/MM/YYYY" placeholder="Selecione a data" />
                <Button type="primary" loading={salvando} onClick={salvar}>Salvar</Button>
                <Button onClick={() => { setEditando(false); setNovaData(cfg?.data_cut_over ? dayjs(cfg.data_cut_over) : null); }}>
                  Cancelar
                </Button>
              </>
            ) : (
              <>
                <Statistic title="Data de cut-over" value={dataAtual}
                  valueStyle={{ fontSize: 16, color: cfg?.data_cut_over ? '#0930D0' : '#B91626' }} />
                <Button onClick={() => setEditando(true)}>Alterar</Button>
              </>
            )}
          </Flex>

          {cfg?.atualizado_em && !editando && (
            <Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 6 }}>
              última alteração: {new Date(cfg.atualizado_em).toLocaleString('pt-BR')} por {cfg.atualizado_por || '—'}
            </Text>
          )}
        </div>

        <div>
          {cfg?.sync_pausado ? (
            <Tag color="error" style={{ fontSize: 12, padding: '4px 10px' }}>⏸ Sync PAUSADO</Tag>
          ) : (
            <Tag color="success" style={{ fontSize: 11 }}>● Sync ativo</Tag>
          )}
          <div style={{ marginTop: 8 }}>
            <Button size="small" type={cfg?.sync_pausado ? 'primary' : 'default'} danger={!cfg?.sync_pausado}
              loading={loading}
              onClick={() => togglePausa(!cfg?.sync_pausado)}>
              {cfg?.sync_pausado ? 'Retomar' : 'Pausar'}
            </Button>
          </div>
        </div>
      </Flex>
    </Card>
  );
}
